import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Connexion réussie!');
      navigate('/dashboard');
    } catch (error) {
      // Check if account is disabled
      if (error.response?.data?.detail === 'account_disabled') {
        toast.error('Votre compte a été désactivé. Veuillez contacter le Back Office pour toute question.', {
          duration: 6000,
        });
      } else if (error.response && [400, 401, 403].includes(error.response.status)) {
        toast.error('Identifiants invalides');
      } else {
        // 500/502/504 ou réseau : ce n'est pas un problème de mot de passe
        const code = error.response?.status || 'réseau';
        toast.error(`Serveur momentanément indisponible (${code}). Réessayez dans quelques instants.`, { duration: 7000 });
      }
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Veuillez saisir votre adresse email');
      return;
    }

    setResetLoading(true);
    try {
      await axios.post(`${API_URL}/auth/request-reset`, { email: resetEmail });
      toast.success('Un email de réinitialisation a été envoyé si le compte existe');
      setIsResetOpen(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'email');
      console.error('Reset password error:', error);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className='min-h-svh grid md:grid-cols-2'>
      <div 
        className='hidden md:block relative bg-gradient-to-br from-teal-50 via-cyan-50 to-slate-50'
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1673236394928-fdd8ec9d3619?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className='absolute inset-0 bg-gradient-to-br from-teal-900/60 via-cyan-900/40 to-slate-900/60'></div>
        <div className='absolute inset-0 flex flex-col items-center justify-center text-white p-8'>
          <h1 className='text-4xl font-semibold mb-4'>RT-Distribution.app</h1>
          <p className='text-lg text-center max-w-md'>CRM et Cockpit de pilotage des ventes</p>
        </div>
      </div>
      
      <div className='flex items-center justify-center p-6'>
        <div className='w-full max-w-sm' data-testid='login-card'>
          {/* Logo RT */}
          <div className='flex justify-center mb-6'>
            <img 
              src='/RT.svg' 
              alt='RT Distribution Logo' 
              className='h-50 w-auto'
              style={{ height: '200px' }}
            />
          </div>
          
          <h1 className='text-2xl font-semibold'>Connexion</h1>
          <p className='text-sm text-muted-foreground mt-1'>Accédez à votre tableau de bord</p>
          
          <form onSubmit={handleSubmit} className='mt-6 space-y-4' data-testid='login-form'>
            <div>
              <label className='text-sm font-medium'>Email</label>
              <Input
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className='mt-1'
                required
                data-testid='login-form-email-input'
              />
            </div>
            
            <div>
              <label className='text-sm font-medium'>Mot de passe</label>
              <div className='relative mt-1'>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='pr-10'
                  required
                  data-testid='login-form-password-input'
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? (
                    <EyeOff className='size-4' />
                  ) : (
                    <Eye className='size-4' />
                  )}
                </button>
              </div>
            </div>
            
            <Button
              type='submit'
              className='w-full'
              disabled={loading}
              data-testid='login-form-submit-button'
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          {/* Lien Mot de passe oublié */}
          <div className='mt-4 text-center'>
            <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
              <DialogTrigger asChild>
                <button
                  type='button'
                  className='text-sm text-primary hover:underline'
                >
                  Mot de passe oublié ?
                </button>
              </DialogTrigger>
              <DialogContent className='sm:max-w-md'>
                <DialogHeader>
                  <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleForgotPassword} className='space-y-4 mt-4'>
                  <div>
                    <label className='text-sm font-medium'>Adresse email</label>
                    <Input
                      type='email'
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder='votre.email@exemple.com'
                      className='mt-1'
                      required
                    />
                    <p className='text-xs text-muted-foreground mt-2'>
                      Un lien de réinitialisation sera envoyé à cette adresse si un compte existe.
                    </p>
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      className='flex-1'
                      onClick={() => setIsResetOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button
                      type='submit'
                      className='flex-1'
                      disabled={resetLoading}
                    >
                      {resetLoading ? 'Envoi...' : 'Envoyer'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}